import { profileUser } from '@/mocks/profile';
import { useFavorites } from '@/hooks/useFavorites';
import { useSavedPromos } from '@/hooks/useSavedPromos';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';

type Tab = 'orders' | 'bonus' | 'favorites' | 'settings' | 'promocodes' | 'savedpromos' | 'history';

interface Props {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
}

const ProfileSidebar = ({ activeTab, onTabChange }: Props) => {
  const user = profileUser;
  const color = '#f0ff00';
  const progress = (user.bonusPoints / user.nextLevelPoints) * 100;
  const { items: favoriteItems } = useFavorites();
  const { count: savedPromosCount } = useSavedPromos();
  const { items: historyItems } = useRecentlyViewed();

  const TABS: { id: Tab; icon: string; label: string; badge?: number; color?: string }[] = [
    { id: 'orders', icon: 'ri-shopping-bag-3-line', label: 'Мои заказы', badge: 5 },
    { id: 'bonus', icon: 'ri-vip-crown-line', label: 'Бонусная карта' },
    { id: 'favorites', icon: 'ri-heart-line', label: 'Избранное', badge: favoriteItems.length > 0 ? favoriteItems.length : undefined },
    { id: 'history', icon: 'ri-history-line', label: 'История просмотров', color: '#00f5ff', badge: historyItems.length > 0 ? historyItems.length : undefined },
    { id: 'promocodes', icon: 'ri-coupon-3-line', label: 'Промокоды', color: '#ff2d78' },
    { id: 'savedpromos', icon: 'ri-bookmark-line', label: 'Сохранённые акции', color: '#ff2d78', badge: savedPromosCount > 0 ? savedPromosCount : undefined },
    { id: 'settings', icon: 'ri-settings-3-line', label: 'Настройки' },
  ];

  return (
    <aside className="w-full lg:w-72 shrink-0 flex flex-col gap-4">
      {/* User card */}
      <div
        className="rounded-2xl p-6 relative overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(240,255,0,0.2)' }}
      >
        <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-8" style={{ background: color, filter: 'blur(40px)', transform: 'translate(30%, -30%)' }} />

        {/* Avatar + level */}
        <div className="flex items-start gap-4 mb-5 relative z-10">
          <div className="relative shrink-0">
            <div className="w-16 h-16 rounded-2xl overflow-hidden" style={{ border: `2px solid ${color}`, boxShadow: `0 0 15px ${color}40` }}>
              <img src={user.avatar} alt={user.name} className="w-full h-full object-cover object-top" />
            </div>
            <div
              className="absolute -bottom-1.5 -right-1.5 px-1.5 py-0.5 rounded-full font-orbitron font-black text-[9px]"
              style={{ background: color, color: '#050508' }}
            >
              {user.level}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-orbitron font-bold text-sm text-white leading-tight mb-0.5 truncate">{user.name}</div>
            <div className="font-mono text-[11px] text-white/40 truncate">{user.email}</div>
            <div className="font-mono text-[11px] text-white/30 mt-0.5">С нами с {user.memberSince}</div>
          </div>
        </div>

        {/* Bonus points */}
        <div className="p-3 rounded-xl mb-4 relative z-10" style={{ background: `${color}08`, border: `1px solid ${color}20` }}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] text-white/40 tracking-widest">БОНУСНЫЕ БАЛЛЫ</span>
            <span className="font-mono text-[10px]" style={{ color }}>1 балл = 1 ₽</span>
          </div>
          <div className="font-orbitron font-black text-2xl mb-2" style={{ color, textShadow: `0 0 15px ${color}60` }}>
            {user.bonusPoints.toLocaleString()}
          </div>
          <div className="h-1.5 rounded-full overflow-hidden mb-1" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(progress, 100)}%`, background: `linear-gradient(90deg, ${color}, #ff6b00)`, boxShadow: `0 0 8px ${color}` }} />
          </div>
          <div className="flex justify-between font-mono text-[9px] text-white/25">
            <span>GOLD</span>
            <span>До PLATINUM: {(user.nextLevelPoints - user.bonusPoints).toLocaleString()} б.</span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 relative z-10">
          {[
            { label: 'Заказов', value: user.ordersCount },
            { label: 'Потрачено', value: `${(user.totalSpent / 1000).toFixed(0)}к ₽` },
          ].map((s, i) => (
            <div key={i} className="flex flex-col items-center p-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="font-orbitron font-black text-lg text-white">{s.value}</div>
              <div className="font-mono text-[10px] text-white/30">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <nav className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {TABS.map((tab, i) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className="w-full flex items-center gap-3 px-5 py-4 cursor-pointer transition-all duration-200 text-left"
              style={{
                background: isActive ? 'rgba(255,45,120,0.08)' : 'transparent',
                borderBottom: i < TABS.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                borderLeft: isActive ? '3px solid #ff2d78' : '3px solid transparent',
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className={tab.icon} style={{ color: isActive ? '#ff2d78' : 'rgba(255,255,255,0.35)', fontSize: '16px' }} />
              </div>
              <span className="font-mono text-sm flex-1" style={{ color: isActive ? '#ff2d78' : 'rgba(255,255,255,0.6)' }}>
                {tab.label}
              </span>
              {tab.badge && (
                <span className="w-5 h-5 flex items-center justify-center rounded-full font-mono text-[10px] font-bold shrink-0" style={{ background: isActive ? '#ff2d78' : 'rgba(255,255,255,0.08)', color: isActive ? '#fff' : 'rgba(255,255,255,0.4)' }}>
                  {tab.badge}
                </span>
              )}
              <i className="ri-arrow-right-s-line text-white/20 shrink-0" />
            </button>
          );
        })}
      </nav>

      {/* Logout */}
      <button
        className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-3 rounded-xl font-mono text-sm cursor-pointer transition-all duration-200"
        style={{ border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)' }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#ff2d78'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,45,120,0.3)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.3)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; }}
      >
        <i className="ri-logout-box-line" />
        Выйти из аккаунта
      </button>
    </aside>
  );
};

export default ProfileSidebar;
